/**
 * Aceleres: reconhece um GIF entre os arquivos anexados no compositor.
 *
 * A regra é a origem não importar: **qualquer vídeo com até
 * GIF_MAX_DURATION_SECONDS e sem faixa de áudio é um GIF**, venha ele do
 * WhatsApp, da galeria ou de onde for. Um `.gif` de verdade entra direto.
 *
 * Ela existe porque o WhatsApp não transporta GIF: guarda e envia um vídeo
 * mudo. Quem salva um GIF de lá recebe "WhatsApp Video ....mp4" e, a partir
 * daí, não há nada no arquivo que diga "isto é um GIF" — só a forma.
 *
 * A mesma heurística vive no servidor (ChatwootService), para a mídia que
 * chega. Precisa existir dos dois lados porque são pontos de entrada
 * diferentes — bytes baixados da WAME no PHP, File local do navegador aqui —
 * e não há runtime comum para partilhar o código.
 *
 * Consequência assumida: um vídeo curto e genuinamente mudo é classificado
 * como GIF. É o que a regra pede.
 */

export const GIF_MAX_DURATION_SECONDS = 10;

// Ler o ficheiro inteiro só se justifica em algo do tamanho de um GIF. Acima
// disto assumimos vídeo e nem tocamos nos bytes.
const MAX_SCAN_BYTES = 24 * 1024 * 1024;
const METADATA_TIMEOUT_MS = 4000;

const fileName = file => file?.name || file?.filename || '';
const fileMime = file => (file?.type || file?.content_type || '').toLowerCase();

export const isGifFile = file =>
  fileMime(file) === 'image/gif' || /\.gif$/i.test(fileName(file));

// Qualquer contêiner de vídeo entra na regra, não só MP4.
const isVideoFile = file => {
  const mime = fileMime(file);
  if (mime.startsWith('video/')) return true;
  // Um MP3 renomeado pela extensao do mime chega como `.mpeg` e casava com o
  // padrao de nome abaixo (`mpe?g`). O <video> ainda le a duracao de um audio,
  // e os marcadores de faixa procurados sao de MP4/Matroska — nenhum existe
  // num MP3 —, entao o arquivo passava por "video curto e mudo" e era enviado
  // como GIF. Quando o mime diz audio, o nome nao decide.
  if (mime.startsWith('audio/')) return false;
  return /\.(3gp|avi|m4v|mkv|mov|mp4|mpe?g|ogv|webm|wmv)$/i.test(fileName(file));
};

const readDurationInSeconds = file =>
  new Promise(resolve => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    let settled = false;
    let timeoutId = null;

    const finish = value => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      URL.revokeObjectURL(url);
      resolve(value);
    };

    timeoutId = window.setTimeout(() => finish(null), METADATA_TIMEOUT_MS);

    video.preload = 'metadata';
    video.muted = true;
    video.onloadedmetadata = () =>
      finish(Number.isFinite(video.duration) ? video.duration : null);
    video.onerror = () => finish(null);
    video.src = url;
  });

// Procura os quatro bytes ASCII do marcador dentro do buffer.
const containsMarker = (bytes, marker) => {
  const needle = [...marker].map(char => char.charCodeAt(0));
  const limit = bytes.length - needle.length;

  for (let index = 0; index <= limit; index += 1) {
    let matched = true;
    for (let offset = 0; offset < needle.length; offset += 1) {
      if (bytes[index + offset] !== needle[offset]) {
        matched = false;
        break;
      }
    }
    if (matched) return true;
  }

  return false;
};

/**
 * Marcadores de faixa de áudio nos contêineres que um navegador ou telemóvel
 * produz na prática. MP4/MOV (ISO BMFF) declaram o handler 'soun' e o codec
 * 'mp4a'; WebM/Matroska declaram o CodecID em texto. Um vídeo sem áudio não
 * carrega nenhum deles.
 *
 * A varredura só roda em arquivo curto, então a hipótese de um destes bytes
 * aparecer por acaso dentro do fluxo comprimido é remota — e, se aparecer, o
 * erro é para o lado seguro: continua sendo tratado como vídeo.
 */
const AUDIO_MARKERS = [
  'soun',
  'mp4a',
  'A_OPUS',
  'A_VORBIS',
  'A_AAC',
  'A_MPEG',
  'A_PCM',
];

export const hasAudioMarkers = bytes =>
  AUDIO_MARKERS.some(marker => containsMarker(bytes, marker));

const hasAudioTrack = async file => {
  if (file.size > MAX_SCAN_BYTES) return true;

  try {
    return hasAudioMarkers(new Uint8Array(await file.arrayBuffer()));
  } catch {
    // Sem conseguir ler, o seguro é tratar como vídeo.
    return true;
  }
};

export const looksLikeGif = async file => {
  if (!file) return false;
  if (isGifFile(file)) return true;
  if (!isVideoFile(file)) return false;

  const duration = await readDurationInSeconds(file);
  if (duration === null || duration > GIF_MAX_DURATION_SECONDS) return false;

  return !(await hasAudioTrack(file));
};

/**
 * Aceleres 2026-08-14: o eco de saida da WAME as vezes cria a mensagem sem
 * `wame_media_type` (visto na conversa 670: um GIF de 1,33s gravado sem
 * marcador nenhum). Sem o campo, a bolha caia em video, com controles.
 *
 * Nao e preciso baixar nada para decidir: o proprio Chatwoot ja analisa o
 * blob e publica `duration` e `has_audio` no payload do anexo. A regra
 * aplicada aqui e a MESMA de `looksLikeGif` (curto + mudo), so que sobre
 * metadados ja calculados no servidor.
 *
 * So decide quando ha resposta: anexo ainda nao analisado chega com os dois
 * campos vazios e continua sendo video.
 */
const isSilentShortVideo = attachment => {
  const fileType = String(
    attachment?.file_type ?? attachment?.fileType ?? ''
  ).toLowerCase();
  if (fileType !== 'video') return false;

  const hasAudio = attachment?.has_audio ?? attachment?.hasAudio;
  if (hasAudio !== false) return false;

  const duration = Number(attachment?.duration);
  return duration > 0 && duration <= GIF_MAX_DURATION_SECONDS;
};

/**
 * Aceleres 2026-08-17: a mesma pergunta feita sobre um ANEXO solto, sem a
 * mensagem em volta. O painel de contato (ContactInfo.vue) lista anexos vindos
 * de `conversations/attachments.json.jbuilder`, que ja carrega o
 * `wame_media_type` da mensagem dentro do proprio anexo — entao o marcador do
 * servidor continua sendo a resposta principal, como em `isGifMessage`.
 *
 * Cuidado com o payload desse endpoint: ele NAO manda `content_type`, e o
 * `data_url` e o endpoint guardado (`.../attachment_downloads/<id>`), sem
 * extensao nenhuma no fim. Quem sobra pro .gif arrastado do computador e o
 * `extension`/`file_name`.
 */
export const isGifAttachment = attachment => {
  if (!attachment) return false;

  const mediaType = String(
    attachment.wame_media_type ?? attachment.wameMediaType ?? ''
  ).toLowerCase();
  if (mediaType === 'gif') return true;
  // Marcador presente e diferente de gif (sticker, video, image) ja decidiu.
  if (mediaType) return false;

  const extension = String(attachment.extension ?? '').toLowerCase();
  const contentType = String(
    attachment.content_type ?? attachment.contentType ?? ''
  ).toLowerCase();
  const name = String(
    attachment.file_name ?? attachment.fileName ?? ''
  ).toLowerCase();

  return (
    extension === 'gif' ||
    contentType === 'image/gif' ||
    name.endsWith('.gif') ||
    isSilentShortVideo(attachment)
  );
};

/**
 * Aceleres 2026-08-12: a mesma pergunta — "isto e um GIF?" — mas feita sobre uma
 * MENSAGEM ja gravada, e nao sobre um File do compositor.
 *
 * Existe porque o tipo do anexo nao responde: o GIF que o WhatsApp manda tem
 * `file_type: 'video'` (e um MP4 mudo de verdade) e um .gif tem
 * `file_type: 'image'`. Pelo anexo sozinho os dois sao indistinguiveis de um
 * video ou de uma foto normal.
 *
 * Quem responde e `wame_media_type` no content_attributes, o campo que o
 * servidor grava ao classificar a midia — o mesmo que as bolhas leem para
 * escolher o componente. O nome do ficheiro entra como reforco para o .gif de
 * verdade, que pode chegar por outro caminho (arrastado do computador) e nao ter
 * esse campo.
 *
 * Aceita as duas grafias das chaves porque a mensagem chega ora crua do store
 * (snake_case), ora transformada (camelCase).
 */
export const isGifMessage = message => {
  if (!message) return false;

  const attributes =
    message.content_attributes ?? message.contentAttributes ?? {};
  const mediaType = String(
    attributes.wame_media_type ?? attributes.wameMediaType ?? ''
  ).toLowerCase();

  if (mediaType === 'gif') return true;
  if (mediaType) return false;

  return (message.attachments ?? []).some(isGifAttachment);
};
